// 상단 시세. 흐르는 띠와 "오늘의 거래 기준가" 표가 같이 쓴다.
//
// ★ 이 파일은 세 저장소에 "복사"해서 쓴다.
//     homepage / goldnaramall-client / mediahub-client
//   고칠 때는 세 곳을 모두 같이 고칠 것. SiteHeader.vue 와 같은 규칙이다.
//
// 값은 자사몰 서버가 준다 — `GET {API_BASE}/api/v1/market-prices/header`.
// 공용 서버(common-server)가 시세와 수수료를 쥐고 있지만 브라우저는 거기에 직접
// 닿지 못한다: /internal/** 이 X-Api-Key 를 요구하고 ALB 가 우리 NAT 주소만
// 통과시킨다. 키를 번들에 실을 수는 없으니 자사몰 서버가 중계한다.
//
// 숫자는 **이미 꾸며진 문자열**("709,317원")로 온다. 헤더가 세 저장소에 복사돼
// 있어서 꾸미는 규칙을 화면에 두면 세 벌을 똑같이 고쳐야 하고, 한 곳만 고치면
// 사이트마다 상단이 달라진다. 반올림도 마찬가지다 — 살 때는 올리고 팔 때는 내리는
// 규칙이 서버 한 곳에만 있어야 네 화면의 끝자리가 같다.
//
// 못 받으면 **퍼블리싱 값을 그대로 둔다.** 상단이 빈 채로 뜨는 것보다 낫고,
// 헤더가 가장 먼저 그려지는 자리라 흔들리면 눈에 띈다.
import { onBeforeUnmount, ref } from 'vue'

const API_BASE = (import.meta.env.VITE_MALL_API_BASE_URL || '').replace(/\/+$/, '')
const URL = `${API_BASE}/api/v1/market-prices/header`

// 서버가 20초 캐시를 들고 있다. 그보다 자주 물어 봐야 같은 값을 다시 받을 뿐이다.
const REFRESH_MS = 60_000
// 시세 띠는 첫 화면에 바로 떠야 한다. 늦으면 퍼블리싱 값으로 그리고 다음 주기를 기다린다.
const TIMEOUT_MS = 4_000

// 한 페이지에 헤더가 둘 이상 있을 수 있다(데스크톱·모바일 헤더가 같이 마운트된다).
// 상태를 모듈에 두어 한 번만 받아 나눠 쓴다.
const rates = ref(null)
const ratesTable = ref(null)
const asOf = ref('')
// 한 번이라도 받았는지. 받기 전에는 화면이 퍼블리싱 값을 그린다.
const loaded = ref(false)

let subscribers = 0
let timer = 0
let inFlight = null
// 마지막으로 **시도한** 시각. 성공·실패를 가리지 않는다.
let lastTry = 0

/**
 * 같은 요청을 겹쳐 보내지 않는다.
 *
 * `inFlight` 만으로는 모자라다 — 한 화면에 헤더가 여러 번 마운트되는데(홈은 연출
 * 중에 그렇다) 실패가 즉시 돌아오면 그 사이 `inFlight` 가 이미 비어 있다.
 * 2026-10-19 에 CORS 가 막힌 상태로 재 보니 **4초에 여섯 번** 나갔다.
 * 실패는 빠르게 돌아오므로 시간으로도 막아야 한다.
 */
const MIN_GAP_MS = 5_000

async function fetchOnce(force = false) {
  if (inFlight) return inFlight
  if (!force && Date.now() - lastTry < MIN_GAP_MS) return
  lastTry = Date.now()
  const control = new AbortController()
  const bell = setTimeout(() => control.abort(), TIMEOUT_MS)

  inFlight = (async () => {
    try {
      const res = await fetch(URL, { signal: control.signal, headers: { Accept: 'application/json' } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = await res.json()
      // 자사몰 서버의 성공 코드는 0 이다(ResultCode.SUCCESS). 200 이 아니다.
      if (body?.code !== 0) throw new Error(body?.message || '시세를 받지 못했습니다.')
      if (body.ticker?.length) rates.value = body.ticker
      if (body.table?.length) ratesTable.value = body.table
      if (body.asOf) asOf.value = body.asOf
      loaded.value = true
    } catch (e) {
      // 조용히 지나간다. 값이 없으면 화면이 퍼블리싱 값을 그대로 쓴다.
      if (import.meta.env.DEV) console.warn('상단 시세를 받지 못했다', e)
    } finally {
      clearTimeout(bell)
      inFlight = null
    }
  })()
  return inFlight
}

/**
 * 상단 시세를 구독한다. 마운트된 헤더가 하나도 없으면 주기 갱신을 멈춘다.
 *
 * @returns {{rates: import('vue').Ref, ratesTable: import('vue').Ref,
 *            asOf: import('vue').Ref<string>, loaded: import('vue').Ref<boolean>}}
 *   받기 전에는 rates·ratesTable 이 null 이다 — 화면이 제 기본값을 쓰라는 뜻이다.
 */
export function useHeaderRates() {
  subscribers += 1
  fetchOnce()
  if (!timer) {
    // 주기 갱신은 간격이 이미 넉넉하므로 시간 제한을 건너뛴다.
    timer = setInterval(() => fetchOnce(true), REFRESH_MS)
  }

  onBeforeUnmount(() => {
    subscribers -= 1
    if (subscribers <= 0) {
      clearInterval(timer)
      timer = 0
      subscribers = 0
    }
  })

  return { rates, ratesTable, asOf, loaded }
}
